import type { PackSignatureStatus } from '@/domain/Models';
import { fromBase64, toBase64, utf8 } from './Crypto';

export type TrustProfile = 'DEBUG' | 'RELEASE';

export interface TrustedPackKey {
  keyId: string;
  publicKeyBase64: string;
  status: 'ACTIVE' | 'REVOKED' | 'TEST';
}

export type SignatureVerifyResult =
  | { status: 'Accepted' }
  | { status: 'LegacyUnsigned' }
  | { status: 'InvalidSignature' }
  | { status: 'UntrustedSigningKey'; keyId: string }
  | { status: 'SigningKeyRevoked'; keyId: string }
  | { status: 'MalformedSignatureFile'; field: string };

export const SIGNATURE_DOMAIN = 'BESS_PACK_SIGNATURE_V1';
export const SIGNATURE_FILE = 'signature.ed25519';

export function parseSignatureFile(
  text: string,
): { algorithm: string; keyId: string; signatureBase64: string } | null {
  const values = new Map<string, string>();
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const separator = line.indexOf('=');
    if (separator <= 0) return null;
    values.set(line.slice(0, separator), line.slice(separator + 1));
  }
  const algorithm = values.get('algorithm');
  const keyId = values.get('keyId');
  const signature = values.get('signature');
  if (!algorithm || keyId === undefined || !signature) return null;
  return { algorithm, keyId, signatureBase64: signature };
}

export async function signatureInput(
  manifestBytes: Uint8Array,
  checksumBytes: Uint8Array,
): Promise<Uint8Array> {
  const domain = utf8(SIGNATURE_DOMAIN);
  const manHash = new Uint8Array(await crypto.subtle.digest('SHA-256', manifestBytes));
  const sumHash = new Uint8Array(await crypto.subtle.digest('SHA-256', checksumBytes));
  const out = new Uint8Array(domain.length + manHash.length + sumHash.length);
  out.set(domain, 0);
  out.set(manHash, domain.length);
  out.set(sumHash, domain.length + manHash.length);
  return out;
}

function lookupKey(
  keys: TrustedPackKey[],
  keyId: string,
  profile: TrustProfile,
): { kind: 'trusted'; key: TrustedPackKey } | { kind: 'unknown' } | { kind: 'revoked' } {
  const match = keys.find((k) => k.keyId === keyId);
  if (!match) return { kind: 'unknown' };
  if (match.status === 'REVOKED') return { kind: 'revoked' };
  if (match.status === 'TEST' && profile === 'RELEASE') return { kind: 'unknown' };
  return { kind: 'trusted', key: match };
}

export async function verifyPackSignature(args: {
  manifestBytes: Uint8Array;
  checksumBytes: Uint8Array;
  signatureText: string | null;
  schemaVersion: number;
  keys: TrustedPackKey[];
  profile: TrustProfile;
  allowLegacyUnsigned: boolean;
  signedSchemaFrom?: number;
}): Promise<SignatureVerifyResult> {
  const signedSchemaFrom = args.signedSchemaFrom ?? 4;
  const requireSignature =
    !args.allowLegacyUnsigned ||
    args.schemaVersion >= signedSchemaFrom ||
    args.profile === 'RELEASE';

  if (!args.signatureText) {
    return requireSignature ? { status: 'InvalidSignature' } : { status: 'LegacyUnsigned' };
  }

  const parsed = parseSignatureFile(args.signatureText);
  if (!parsed) return { status: 'MalformedSignatureFile', field: 'unreadable' };
  if (parsed.algorithm !== 'Ed25519') return { status: 'MalformedSignatureFile', field: 'algorithm' };
  if (!parsed.keyId) return { status: 'MalformedSignatureFile', field: 'keyId' };

  const found = lookupKey(args.keys, parsed.keyId, args.profile);
  if (found.kind === 'unknown') return { status: 'UntrustedSigningKey', keyId: parsed.keyId };
  if (found.kind === 'revoked') return { status: 'SigningKeyRevoked', keyId: parsed.keyId };

  let publicKey: Uint8Array;
  let signature: Uint8Array;
  try {
    publicKey = fromBase64(found.key.publicKeyBase64);
    signature = fromBase64(parsed.signatureBase64);
  } catch {
    return { status: 'MalformedSignatureFile', field: 'signature' };
  }

  const { verifyAsync } = await import('@noble/ed25519');
  const message = await signatureInput(args.manifestBytes, args.checksumBytes);
  const ok = await verifyAsync(signature, message, publicKey);
  return ok ? { status: 'Accepted' } : { status: 'InvalidSignature' };
}

export function toPackSignatureStatus(result: SignatureVerifyResult): PackSignatureStatus {
  switch (result.status) {
    case 'Accepted':
      return 'TRUSTED';
    case 'LegacyUnsigned':
      return 'LEGACY_UNSIGNED';
    case 'SigningKeyRevoked':
      return 'REVOKED_KEY';
    case 'UntrustedSigningKey':
      return 'UNKNOWN_KEY';
    default:
      return 'INVALID';
  }
}

export { toBase64 };
